import { useNavigate, useParams } from 'react-router';
import {
    Autocomplete,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    Paper,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Divider,
    Chip,
    Alert,
    CircularProgress,
    useTheme,
    alpha,
    Fade,
    Zoom,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    West,
    Add,
    Save,
    FlightTakeoff,
    Hotel,
    DirectionsCar,
    Hiking,
    AccountBalance,
    Receipt,
    Delete,
    AttachMoney,
    Description,
    LocationOn,
    People,
    Event,
    Schedule,
    Calculate
} from '@mui/icons-material';
import { COLORS, output, priceFormatter, toEuro } from '../../settings/themeSettings';
import { DateField, LocalizationProvider, TimeField, frFR } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useState, useCallback, useMemo } from 'react';
import EditableTable from '../../components/editableTable/EditableTable';
import Circuit from '../../data/schemas/circuit';
import Person from '../../data/schemas/person';
import Excursion from '../../data/schemas/excursion';
import Transfert from '../../data/schemas/transfert';
import Location from '../../data/schemas/location';
import Frais from '../../data/schemas/frais';
import Group from '../../data/schemas/group';
import Envelop from '../../data/schemas/envelop';
import { useDispatch, useSelector } from 'react-redux';
import { fetchChauffeur, createChauffeur, addChauffeur } from '../../data/slices/chauffeurSlice';
import { fetchGuides, createGuide, addGuide } from '../../data/slices/guideSlice';
import { fetchVehicules, createVehicule, addVehicule } from '../../data/slices/vehiculeSlice';
import { fetchHotels, createHotel, addHotel } from '../../data/slices/hotelSlice';
import { fetchAgences, createAgence, addAgence } from '../../data/slices/agenceSlice';
import { fetchTypeExcursion, createTypeExcursion, addTypeExcursion } from '../../data/slices/typeExcursionSlice';
import DossierCircuit from '../../data/schemas/dossierCircuit';
import { createCircuit, fetchCircuit, fetchCircuits } from '../../data/slices/circuitSlice';
import { notify } from '../../settings/toastSetting';
import { fetchTypeFrais, createTypeFrais, addTypeFrais } from '../../data/slices/typeFraisSlice';
import { fetchProgrammes, createProgram, addProgram } from '../../data/slices/programmeSlice';
import { fetchToClient, createToClient, addToClient, clearLastCreatedToClient } from '../../data/slices/toClientSlice';
import dayjs from 'dayjs';
import Sejour from '../../data/schemas/sejour';
import ClientForm from '../../components/forms/ClientForm';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import Rooming from '../../components/Rooming/Rooming';
import Chauffeur from '../../data/schemas/chauffeur';
import Guide from '../../data/schemas/guide';
import BaseParameter from '../../data/schemas/baseParameter';
import Vehicule from '../../data/schemas/vehicule';
import HotelSchema from '../../data/schemas/hotel';
import Agence from '../../data/schemas/Agence';
import ToClient from '../../data/schemas/toClient';
import QuickAddForm from '../../components/TransForm/Transform';

const ListFrais = ({ frais = [], onFraisUpdate }) => {
    const reference = useMemo(() => new Frais(), []);
    const readOnlyProps = useMemo(() => ["frais", "montant"], []);

    return <div>
        <EditableTable 
            data={frais} 
            reference={reference} 
            onUpdate={onFraisUpdate} 
            showButton={false} 
            readOnlyProps={readOnlyProps}
        />
    </div>;
};

const Enveloppe = ({ enveloppes = [], onEnvelopUpdate }) => {
    const operations = useMemo(() => ({
        ecart: { labels: ['montantEstime', 'montantReel'], type: 'diff' }
    }), []);

    const readOnlyProps = useMemo(() => ['ecart'], []);
    const reference = useMemo(() => new Envelop(), []);

    return <div>
        <EditableTable 
            data={enveloppes} 
            reference={reference} 
            onUpdate={onEnvelopUpdate}
            operations={operations}
            readOnlyProps={readOnlyProps}
        />
    </div>;
};

const Locations = ({ 
    locations = [], 
    onLocationUpdate,
    chauffeur = null,
    guide = null,
    vehicule = null,
    handleQuickAdd,
    setNewAgenceName, setNewAgenceDialog,
    setNewChauffeurName, setNewChauffeurDialog,
    setNewVehiculeName, setNewVehiculeDialog,
    setNewGuideName, setNewGuideDialog,
    dossierDates = {},
    setActiveQuickAdd
}) => {
    const { chauffeurs } = useSelector((state) => state.chauffeurs);
    const { guides } = useSelector((state) => state.guides);
    const { vehicules } = useSelector((state) => state.vehicules);
    const { agences } = useSelector((state) => state.agences);

    // Stabilisation des options et choix (UX-UI improvement)
    const choices = useMemo(() => {
        const agenceOptions    = [...(agences   || []), { isAction: 'agence',    nom:       '+ Ajouter une nouvelle agence',   inputValue: '' }];
        const chauffeurOptions = [...(chauffeurs|| []), { isAction: 'chauffeur', nomComplet:'+ Ajouter un nouveau chauffeur',  inputValue: '' }];
        
        // Enrichir les véhicules avec un label complet (Marque - Modèle - Matricule)
        const enrichedVehicules = (vehicules || []).map(v => ({
            ...v,
            labelComplet: `${v.marque || ''} ${v.modele || ''} - ${v.matricule || ''}`.trim().replace(/^ - | - $/g, '')
        }));
        const vehiculeOptions  = [...enrichedVehicules, { isAction: 'vehicule',  labelComplet: '+ Ajouter un nouveau véhicule',   inputValue: '' }];
        
        const guideOptions     = [...(guides    || []), { isAction: 'guide',     nomComplet:'+ Ajouter un nouveau guide',      inputValue: '' }];

        return {
            agence:    { options: agenceOptions,    optionLabel: 'nom',          optionValue: 'id' },
            chauffeur: { options: chauffeurOptions, optionLabel: 'nomComplet',   optionValue: 'id' },
            vehicule:  { options: vehiculeOptions,  optionLabel: 'labelComplet', optionValue: 'id' },
            guide:     { options: guideOptions,     optionLabel: 'nomComplet',   optionValue: 'id' }
        };
    }, [agences, chauffeurs, vehicules, guides]);

    const operations = useMemo(() => ({
        nombreJour: { labels: ['dateDebut', 'dateFin'], type: 'daydiff+' },
        montant: { labels: ['prixUnitaire', 'nombreJour'], type: '*' }
    }), []);

    // Référence avec pré-remplissage (UX-02)
    const defaultRef = useMemo(() => {
        const ref = new Location();
        if (chauffeur) ref.chauffeur = chauffeur;
        if (guide)     ref.guide     = guide;
        if (vehicule)  ref.vehicule  = vehicule;
        if (dossierDates.dateDebut) ref.dateDebut = dayjs(dossierDates.dateDebut);
        if (dossierDates.dateFin)   ref.dateFin   = dayjs(dossierDates.dateFin);
        return ref;
    }, [chauffeur, guide, vehicule, dossierDates.dateDebut, dossierDates.dateFin]);

    return <div>
        <EditableTable 
            data={locations} 
            reference={defaultRef}
            onUpdate={onLocationUpdate}
            choices={choices}
            operations={operations}
            onChoiceChange={(field, value, idx) => {
                setActiveQuickAdd({ table: 'locations', rowIdx: idx, field });
                if (value?.isAction === 'agence')    { setNewAgenceName(value.inputValue || '');    setNewAgenceDialog(true); }
                if (value?.isAction === 'chauffeur') { setNewChauffeurName(value.inputValue || ''); setNewChauffeurDialog(true); }
                if (value?.isAction === 'vehicule')  { setNewVehiculeName(value.inputValue || '');  setNewVehiculeDialog(true); }
                if (value?.isAction === 'guide')     { setNewGuideName(value.inputValue || '');     setNewGuideDialog(true); }
            }}
        />
    </div>;
};

const Excursions = ({ 
    excursions = [], 
    onExcursionUpdate, 
    chauffeur = null, 
    guide = null,
    handleQuickAdd,
    setNewExcursionName, setNewExcursionDialog,
    setNewChauffeurName, setNewChauffeurDialog,
    setNewGuideName, setNewGuideDialog,
    dossierDates = {},
    setActiveQuickAdd
}) => {
    const { typeExcursions } = useSelector((state) => state.typeExcursions);
    const { guides } = useSelector((state) => state.guides);
    const { chauffeurs } = useSelector((state) => state.chauffeurs);

    const choices = useMemo(() => {
        const excursionOptions = [...(typeExcursions || []), { isAction: 'excursion', libelle: '+ Ajouter un nouveau type d\'excursion', inputValue: '' }];
        const chauffeurOptions = [...(chauffeurs || []), { isAction: 'chauffeur', nomComplet: '+ Ajouter un nouveau chauffeur', inputValue: '' }];
        const guideOptions = [...(guides || []), { isAction: 'guide', nomComplet: '+ Ajouter un nouveau guide', inputValue: '' }];

        return {
            excursion: { options: excursionOptions, optionLabel: 'libelle',    optionValue: 'id' },
            chauffeur: { options: chauffeurOptions, optionLabel: 'nomComplet', optionValue: 'id' },
            guide:     { options: guideOptions,     optionLabel: 'nomComplet', optionValue: 'id' }
        };
    }, [typeExcursions, chauffeurs, guides]);

    const operations = useMemo(() => ({
        montant: { labels: ['pax', 'prixUnitaire'], type: '*' }
    }), []);

    // Référence avec pré-remplissage (UX-02)
    const defaultRef = useMemo(() => {
        const ref = new Excursion();
        if (chauffeur) ref.chauffeur = chauffeur;
        if (guide)     ref.guide     = guide;
        if (dossierDates.dateDebut) ref.date = dayjs(dossierDates.dateDebut);
        return ref;
    }, [chauffeur, guide, dossierDates.dateDebut]);

    return <div>
        <EditableTable 
            data={excursions} 
            reference={defaultRef}
            onUpdate={onExcursionUpdate}
            choices={choices}
            operations={operations}
            onChoiceChange={(field, value, idx) => {
                setActiveQuickAdd({ table: 'excursions', rowIdx: idx, field });
                if (value?.isAction === 'excursion') { setNewExcursionName(value.inputValue || ''); setNewExcursionDialog(true); }
                if (value?.isAction === 'chauffeur') { setNewChauffeurName(value.inputValue || ''); setNewChauffeurDialog(true); }
                if (value?.isAction === 'guide')     { setNewGuideName(value.inputValue || '');     setNewGuideDialog(true); }
            }}
        />
    </div>;
};

const Transferts = ({ 
    transferts = [], 
    onTransfertUpdate,
    chauffeur = null,
    guide = null,
    vehicule = null,
    handleQuickAdd,
    setNewChauffeurName, setNewChauffeurDialog,
    setNewVehiculeName, setNewVehiculeDialog,
    setNewGuideName, setNewGuideDialog,
    dossierDates = {},
    setActiveQuickAdd
}) => {
    const { chauffeurs } = useSelector((state) => state.chauffeurs);
    const { vehicules } = useSelector((state) => state.vehicules);
    const { guides } = useSelector((state) => state.guides);

    const choices = useMemo(() => {
        const chauffeurOptions = [...(chauffeurs || []), { isAction: 'chauffeur', nomComplet: '+ Ajouter un nouveau chauffeur', inputValue: '' }];
        
        // Enrichir les véhicules avec un label complet (Marque - Modèle - Matricule)
        const enrichedVehicules = (vehicules || []).map(v => ({
            ...v,
            labelComplet: `${v.marque || ''} ${v.modele || ''} - ${v.matricule || ''}`.trim().replace(/^ - | - $/g, '')
        }));
        const vehiculeOptions  = [...enrichedVehicules, { isAction: 'vehicule',  labelComplet:  '+ Ajouter un nouveau véhicule',  inputValue: '' }];
        
        const guideOptions     = [...(guides     || []), { isAction: 'guide',     nomComplet: '+ Ajouter un nouveau guide',     inputValue: '' }];

        return {
            chauffeur: { options: chauffeurOptions, optionLabel: 'nomComplet',   optionValue: 'id' },
            vehicule:  { options: vehiculeOptions,  optionLabel: 'labelComplet', optionValue: 'id' },
            guide:     { options: guideOptions,     optionLabel: 'nomComplet',   optionValue: 'id' }
        };
    }, [chauffeurs, vehicules, guides]);

    const operations = useMemo(() => ({
        montant: { labels: ['pax', 'prixUnitaire'], type: '*' }
    }), []);

    // Référence avec pré-remplissage (UX-02)
    const defaultRef = useMemo(() => {
        const ref = new Transfert();
        if (chauffeur) ref.chauffeur = chauffeur;
        if (guide)     ref.guide     = guide;
        if (vehicule)  ref.vehicule  = vehicule;
        if (dossierDates.dateDebut) ref.date = dayjs(dossierDates.dateDebut);
        return ref;
    }, [chauffeur, guide, vehicule, dossierDates.dateDebut]);

    return <div>
        <EditableTable 
            data={transferts} 
            reference={defaultRef}
            onUpdate={onTransfertUpdate}
            choices={choices}
            operations={operations}
            onChoiceChange={(field, value, idx) => {
                setActiveQuickAdd({ table: 'transferts', rowIdx: idx, field });
                if (value?.isAction === 'chauffeur') { setNewChauffeurName(value.inputValue || ''); setNewChauffeurDialog(true); }
                if (value?.isAction === 'vehicule')  { setNewVehiculeName(value.inputValue || '');  setNewVehiculeDialog(true); }
                if (value?.isAction === 'guide')     { setNewGuideName(value.inputValue || '');     setNewGuideDialog(true); }
            }}
        />
    </div>;
};

const Circuits = ({ circuits = [], onCircuitUpdate, handleQuickAdd, setNewHotelName, setNewHotelDialog, dossierDates = {}, setActiveQuickAdd }) => {
    const { hotels } = useSelector((state) => state.hotels);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchHotels());
    }, []);

    const choices = useMemo(() => {
        const hotelOptions = [...(hotels || []), { isAction: 'hotel', nom: '+ Ajouter un nouvel hôtel', inputValue: '' }];
        return {
            hotel: { options: hotelOptions, optionLabel: 'nom', optionValue: 'id' }
        };
    }, [hotels]);

    const operations = useMemo(() => ({
        nuitee: { labels: ['dateDebut', 'dateFin'], type: 'daydiff' },
        montant: { labels: ['pax', 'prixUnitaire', 'nuitee'], type: '*' }
    }), []);

    // Référence avec dates par défaut (UX-02)
    const defaultRef = useMemo(() => {
        const ref = new Sejour();
        if (dossierDates.dateDebut) ref.dateDebut = dayjs(dossierDates.dateDebut);
        if (dossierDates.dateFin)   ref.dateFin   = dayjs(dossierDates.dateFin);
        return ref;
    }, [dossierDates.dateDebut, dossierDates.dateFin]);

    return <div>
        <EditableTable 
            data={circuits} 
            reference={defaultRef} 
            onUpdate={onCircuitUpdate}
            choices={choices}
            operations={operations}
            onChoiceChange={(field, value, idx) => {
                setActiveQuickAdd({ table: 'hotels', rowIdx: idx, field });
                if (value?.isAction === 'hotel') { setNewHotelName(value.inputValue || ''); setNewHotelDialog(true); }
            }}
        />
    </div>;
};

const AjoutCircuitPage = (props) => {
    const theme = useTheme();
    const { type, id } = useParams();
    const navigateTo = useNavigate();
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);
    const [tabs, setTabs] = useState([]);
    const [newProgrammeDialog, setNewProgrammeDialog] = useState(false);
    const [newProgrammeName, setNewProgrammeName] = useState('');
    const [newExcursionDialog, setNewExcursionDialog] = useState(false);
    const [newExcursionName, setNewExcursionName] = useState('');
    const [newChauffeurDialog, setNewChauffeurDialog] = useState(false);
    const [newChauffeurName, setNewChauffeurName] = useState('');
    const [newVehiculeDialog, setNewVehiculeDialog] = useState(false);
    const [newVehiculeName, setNewVehiculeName] = useState('');
    const [newGuideDialog, setNewGuideDialog] = useState(false);
    const [newGuideName, setNewGuideName] = useState('');
    const [newHotelDialog, setNewHotelDialog] = useState(false);
    const [newHotelName, setNewHotelName] = useState('');
    const [newAgenceDialog, setNewAgenceDialog] = useState(false);
    const [newAgenceName, setNewAgenceName] = useState('');
    const [newClientDialog, setNewClientDialog] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newTypeFraisDialog, setNewTypeFraisDialog] = useState(false);
    const [newTypeFraisName, setNewTypeFraisName] = useState('');

    const [circuit, setCircuit] = useState(new DossierCircuit());
    const [circuits, setCircuits] = useState([]);
    const [rooming, setRooming] = useState([]);
    const [excursions, setExcursions] = useState([]);
    const [transferts, setTransferts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [envelops, setEnvelops] = useState([]);
    const [montant, setMontant] = useState(0);
    const [openClientModal, setOpenClientModal] = useState(false);

    const [frais, setFrais] = useState([
        { frais: "HEBERGEMENT", montant: 0 },
        { frais: "LOCATION", montant: 0 },
        { frais: "EXCURSION", montant: 0 },
        { frais: "TRANSFERT", montant: 0 },
    ]);

    // État pour suivre le contexte de création rapide (quelle table, quelle ligne, quel champ)
    const [activeQuickAdd, setActiveQuickAdd] = useState({ table: null, rowIdx: null, field: null });

    // Breadcrumb items personnalisés
    const breadcrumbItems = [
        { label: 'Accueil', path: '/' },
        { label: 'Dossiers', path: `/dossiers/${type}` },
        { label: type === 'excursion' ? `Nouvelle ${type}` : `Nouveau ${type}`, path: `/ajout/dossier/${type}` }
    ];

    const isEdit = !!id;

    // Fonctions utilitaires pour le design moderne
    const getTypeIcon = () => {
        switch(type) {
            case 'circuit': return <FlightTakeoff />;
            case 'sejour': return <Hotel />;
            case 'excursion': return <Hiking />;
            case 'transfert': return <DirectionsCar />;
            default: return <Description />;
        }
    };

    const getPageTitle = () => {
        const prefix = isEdit ? 'Modification' : (type === 'excursion' ? 'Nouvelle' : 'Nouveau');
        return `${prefix} ${type}`;
    };

    const getPageDescription = () => {
        return isEdit ? `Modification du dossier ${circuit.reference || ''}` : `Création d'un nouveau dossier de type ${type}`;
    };

    const getTypeColor = () => {
        switch(type) {
            case 'circuit': return COLORS.primary;
            case 'sejour': return COLORS.secondary;
            case 'excursion': return '#4caf50';
            case 'transfert': return '#ff9800';
            default: return COLORS.tertiary;
        }
    };

    const onCircuitUpdate = useCallback(_ => setCircuits(_), []);
    const onRoomingUpdate = useCallback(_ => setRooming(_), []);
    const onExcursionUpdate = useCallback(_ => setExcursions(_), []);
    const onTransfertUpdate = useCallback(_ => setTransferts(_), []);
    const onLocationUpdate = useCallback(_ => setLocations(_), []);
    const onEnvelopUpdate = useCallback(_ => setEnvelops(_), []);
    const onFraisUpdate = useCallback(_ => setFrais(_), []);
    const onRoomingTypeChanged = useCallback((type) => {
        setCircuit(prev => ({ ...prev, grouped: type }));
    }, []);

    const { chauffeurs, chauffeursFetchStatus } = useSelector((state) => state.chauffeurs);
    const { guides, guidesFetchStatus } = useSelector((state) => state.guides);
    const { vehicules, vehiculeFetchStatus } = useSelector((state) => state.vehicules);
    const { agences, agencesFetchStatus } = useSelector((state) => state.agences);
    const { hotels, hotelsFetchStatus } = useSelector((state) => state.hotels);
    const { typeExcursions, typeExcursionsFetchStatus } = useSelector((state) => state.typeExcursions);
    const { programmes, programmesFetchStatus } = useSelector((state) => state.programmes);
    const { toClients, toClientsFetchStatus, lastCreatedToClient } = useSelector((state) => state.toClients);
    const { allCircuits } = useSelector((state) => state.circuits);
    const { me } = useSelector((state) => state.me);

    // ── Génération automatique de référence ──────────────────────────────────
    // clientType: 'individuel' => insère 'IND' au lieu des initiales du client
    const generateReference = (dossiersType = type, clientObj = circuit.toClient || circuit.nomClient, clientType = circuit.clientType) => {
        // ── Initiales utilisateur connecté ──
        const getUserCode = () => {
            if (me?.code && me.code.trim()) return me.code.trim().toUpperCase();
            if (me?.nomComplet) {
                const words = me.nomComplet.trim().split(/\s+/).filter(w => w.length > 0);
                if (words.length >= 3) return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
                if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
                return words[0].substring(0, 3).toUpperCase().padEnd(3, 'X');
            }
            return 'USR';
        };

        const userCode = getUserCode();

        // ── Initiales client ──
        const getClientCode = (name) => {
            if (!name) return 'CLI';
            const words = name.trim().split(/\s+/).filter(w => w.length > 0);
            if (words.length >= 3) return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
            if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
            return words[0].substring(0, 3).toUpperCase().padEnd(3, 'X');
        };

        // Si c'est un client individuel, on utilise 'IND' comme code client
        let clientCode = 'IND';
        if (clientType !== 'individuel') {
            clientCode = 'CLI';
            if (clientObj) {
                if (typeof clientObj === 'string') {
                    clientCode = getClientCode(clientObj);
                } else if (clientObj.nomComplet) {
                    clientCode = getClientCode(clientObj.nomComplet);
                } else if (clientObj.libelle) {
                    clientCode = getClientCode(clientObj.libelle);
                } else if (clientObj.code && clientObj.code.trim() !== '') {
                    clientCode = clientObj.code.trim().toUpperCase();
                }
            }
        }

        const year = dayjs().format('YY'); // ex: "26"

        // ── Numérotation séquentielle globale pour l'année ──
        const refRegex = new RegExp(`^.*?\\/.*?\\/(\\d+)\\/${year}$`);

        // Trouver le NUMÉRO MAX parmi TOUS les dossiers de l'année
        let maxSeq = 0;
        (allCircuits || []).forEach((c) => {
            const match = (c.reference || '').match(refRegex);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxSeq) maxSeq = num;
            }
        });

        // Si le dossier en cours d'édition a déjà une référence valide, conserver la séquence
        if (id && circuit?.reference) {
            const currentMatch = circuit.reference.match(refRegex);
            if (currentMatch) {
                const seq = currentMatch[1];
                return `${userCode}/${clientCode}/${seq}/${year}`;
            }
        }

        const seq = String(maxSeq + 1).padStart(3, '0');
        return `${userCode}/${clientCode}/${seq}/${year}`;
    };
    // ────────────────────────────────────────────────────────────────────────

    const dispatch = useDispatch();

    // Recalcule la référence dès que la liste des circuits (pour le compteur) et l'utilisateur connecté sont disponibles
    useEffect(() => {
        if (!id && allCircuits?.length > 0 && me) {
            const newRef = generateReference();
            if (newRef && newRef !== circuit.reference) {
                setCircuit(prev => ({ ...prev, reference: newRef }));
            }
        }
    }, [allCircuits, me, id]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Préchargement automatique du client créé ---
    useEffect(() => {
        if (!id && lastCreatedToClient && !circuit.toClient && !circuit.nomClient) {
            try {
                setCircuit(prev => ({ 
                    ...prev, 
                    toClient: lastCreatedToClient
                }));
                
                // On consomme le préchargement pour ne pas qu'il se reproduise à chaque refresh
                dispatch(clearLastCreatedToClient());
                
                notify(`Client ${lastCreatedToClient.libelle} préchargé automatiquement.`, 'info');
            } catch (err) {
                console.error("Erreur lors du préchargement du client:", err);
            }
        }
    }, [lastCreatedToClient, id, dispatch]);
    // ---------------------------------------------------

    useEffect(() => {
        switch (type) {
            case 'circuit': {
                setTabs(['Hebergements', 'Rooming', 'Excursions', 'Transferts', 'Locations', 'Enveloppe', 'Synthèse']);
            } break;

            case 'sejour': {
                setTabs(['Hebergements', 'Rooming', 'Excursions', 'Transferts', 'Locations', 'Enveloppe', 'Synthèse']);
            } break;

            case 'excursion': {
                setTabs(['Excursions', 'Rooming', 'Enveloppe', 'Synthèse']);
            } break;

            case 'transfert': {
                setTabs(['Transferts', 'Synthèse']);
            } break;
        }

        dispatch(fetchChauffeur());
        dispatch(fetchGuides());
        dispatch(fetchVehicules());
        dispatch(fetchAgences());
        dispatch(fetchTypeExcursion());
        dispatch(fetchTypeFrais());
        dispatch(fetchProgrammes());
        dispatch(fetchToClient());
        dispatch(fetchCircuits(type)); // Charge tous les dossiers du même type pour le compteur de séquence

        setCircuit({
            ...circuit,
            date: dayjs(new Date()),
            dateVolArrivee: dayjs(new Date()),
            dateVolDepart: dayjs(new Date()),
        });

        if (id) {
            fetchCircuit(id).then((resp) => {
                return resp.data;
            }).then(async (dossier) => {
                output(dossier);
                setCircuit({ ...dossier, grouped: dossier.isGroup });

                await dossier.rooming.map((r) => {
                    delete r.id;
                    return r;
                });

                setExcursions(dossier.excursions.map((e) => {
                    return new Excursion(dayjs(e.date), e.heure, e.type.id, e.observation, e.chauffeur?.id, e.guide?.id, e.pax, e.prixUnitaire, e.montant);
                }));
                setTransferts(
                    dossier.transferts.map((t) => {
                        return new Transfert(dayjs(t.date), t.heure, t.lieuDepart, t.lieuArrivee, t.vehicule?.id, t.chauffeur?.id, t.guide?.id, t.pax, t.prixUnitaire, t.montant);
                    })
                );
                setLocations(
                    dossier.locations.map((t) => {
                        return new Location(t.agence?.id, dayjs(t.dateDebut), dayjs(t.dateFin), t.nombreJour, t.prixUnitaire, t.vehicule?.id, t.chauffeur?.id, t.guide?.id, t.montant);
                    })
                );
                setRooming(dossier.rooming);
                setCircuits(dossier.hotels.map((h) => {
                    return new Sejour(dayjs(h.dateDebut), dayjs(h.dateFin), h.nuitee, h.hotel?.id, h.prixUnitaire, h.typeChambre, h.regime, h.pax, h.montant, h.categorieChambre);
                }));
                setEnvelops((dossier.envelops || []).map(e => new Envelop(
                    e.frais,
                    e.montant      ?? 0,   // montant DB → montantEstime
                    e.depouillement ?? 0,  // depouillement DB → montantReel
                    (e.montant ?? 0) - (e.depouillement ?? 0) // ecart calculé
                )));
            }).catch((err) => output(err));
        }
    }, [id, type, dispatch]);

    useEffect(() => {
        setTab(tabs[0]);
    }, [tabs]);

    const handleSubmit = () => {
        // Sécurité : si on est en mode édition mais que le dossier n'est pas encore chargé
        if (isEdit && !circuit.id) {
            notify("Le dossier n'est pas encore chargé, veuillez patienter", "warning");
            return;
        }

        setLoading(true);

        // Validation du client (TO Client ou Client Comptant)
        if (!circuit.toClient && !circuit.nomClient) {
            notify("Veuillez sélectionner un client (partenaire ou client comptant)", "error");
            setLoading(false);
            return;
        }

        // Validation programme (obligatoire pour circuit et séjour)
        if ((type === 'circuit' || type === 'sejour') && !circuit.programme) {
            notify("Veuillez sélectionner un programme", "error");
            setLoading(false);
            return;
        }

        // Validation date
        if (!circuit.date) {
            notify("Veuillez saisir la date du dossier", "error");
            setLoading(false);
            return;
        }

        // Validation pax
        if (!circuit.pax || parseInt(circuit.pax, 10) <= 0) {
            notify("Veuillez saisir le nombre de passagers (PAX)", "error");
            setLoading(false);
            return;
        }

        const payload = {
            ...circuit,
            id: isEdit ? (id || circuit.id) : undefined,
            hotels: circuits,
            excursions: excursions,
            transferts: transferts,
            locations: locations,
            envelops: envelops,
            frais: frais,
            rooming: rooming,
            type: type
        };

        createCircuit(payload,
            (err) => {
                setLoading(false);
                const errorData = err.data || {};
                output(errorData);
                if (errorData.error || errorData.detail) {
                    notify(errorData.error || errorData.detail, 'error');
                    return;
                }
                notify("Une erreur s'est produite, actualisez puis réessayez!", 'error');
            },
            (resp) => {
                output(resp);
                setLoading(false);
                const successMsg = isEdit ? "Dossier mis à jour avec succès" : "Dossier créé avec succès";
                notify(successMsg, 'success');
                
                if (isEdit) {
                    navigateTo(`/dossiers/${type}/${id}`);
                } else {
                    navigateTo(`/dossiers/${type}/${resp.id}`);
                }
            });
    };

    const handleCreateProgramme = () => {
        if (!newProgrammeName.trim()) {
            notify('Veuillez entrer un nom de programme', 'error');
            return;
        }

        const name = newProgrammeName.trim();
        createProgram({ libelle: name }, 
            (err) => {
                notify(`Erreur lors de l'enregistrement du programme "${name}".`, 'error');
                console.error(err);
            }, 
            (resp) => {
                // Update local state and selection IMMEDIATELY
                dispatch(addProgram(resp));
                setCircuit({...circuit, programme: resp});
                
                notify(`Le programme "${name}" a été enregistré avec succès !`, 'success');
                setNewProgrammeName('');
                setNewProgrammeDialog(false);
            }
        );
    };

    const handleCreateExcursion = () => {
        if (!newExcursionName.trim()) {
            notify('Veuillez entrer un nom d\'excursion', 'error');
            return;
        }

        const name = newExcursionName.trim();
        createTypeExcursion({ libelle: name }, 
            (err) => {
                notify(`Erreur lors de l'enregistrement de l'excursion "${name}".`, 'error');
                console.error(err);
            }, 
            (resp) => {
                // Update local state IMMEDIATELY
                dispatch(addTypeExcursion(resp));
                
                // Sélection automatique si on est dans le tableau des excursions
                if (activeQuickAdd.table === 'excursions' && activeQuickAdd.field === 'excursion') {
                    const { rowIdx, field } = activeQuickAdd;
                    setExcursions(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: resp.id } : row));
                }
                
                notify(`Le type d'excursion "${name}" a été enregistré avec succès !`, 'success');
                setNewExcursionName('');
                setNewExcursionDialog(false);
            }
        );
    };

    const handleQuickAdd = (type, data) => {
        const createActions = {
            chauffeur: { create: createChauffeur, fetch: fetchChauffeur, label: 'Chauffeur' },
            guide: { create: createGuide, fetch: fetchGuides, label: 'Guide' },
            vehicule: { create: createVehicule, fetch: fetchVehicules, label: 'Véhicule' },
            hotel: { create: createHotel, fetch: fetchHotels, label: 'Hôtel' },
            agence: { create: createAgence, fetch: fetchAgences, label: 'Agence' },
            client: { create: createToClient, fetch: fetchToClient, label: 'Client' },
            typeFrais: { create: createTypeFrais, fetch: fetchTypeFrais, label: 'Type de frais' },
        };

        const action = createActions[type];
        if (!action) return;

        const actionMappings = {
            chauffeur: addChauffeur,
            guide: addGuide,
            vehicule: addVehicule,
            hotel: addHotel,
            agence: addAgence,
            client: addToClient,
            typeFrais: addTypeFrais,
        };

        const itemName = data.nomComplet || data.nom || data.libelle || data.matricule || '';

        action.create(data, 
            (err) => {
                notify(`Erreur lors de l'enregistrement de "${itemName || action.label}".`, 'error');
                console.error(err);
            }, 
            (resp) => {
                // Update local Redux state IMMEDIATELY for instant UI refresh
                const localAction = actionMappings[type];
                if (localAction) {
                    dispatch(localAction(resp));
                } else {
                    dispatch(action.fetch());
                }

                // UX-11 : Sélection automatique de l'élément créé
                if (type === 'chauffeur') {
                    if (activeQuickAdd.table === null) setCircuit(prev => ({ ...prev, chauffeur: resp }));
                    setNewChauffeurDialog(false);
                }
                if (type === 'guide') {
                    if (activeQuickAdd.table === null) setCircuit(prev => ({ ...prev, guide: resp }));
                    setNewGuideDialog(false);
                }
                if (type === 'vehicule') {
                    if (activeQuickAdd.table === null) setCircuit(prev => ({ ...prev, vehicule: resp }));
                    setNewVehiculeDialog(false);
                }
                if (type === 'hotel') {
                    // UX-11 : Sélection automatique de l'hôtel créé dans le tableau
                    if (activeQuickAdd.table === 'hotels') {
                        const { rowIdx, field } = activeQuickAdd;
                        setCircuits(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: resp.id } : row));
                    }
                    setNewHotelDialog(false);
                }
                if (type === 'agence') setNewAgenceDialog(false);
                if (type === 'client') {
                    setNewClientDialog(false);
                    // Préchargement automatique immédiat après création rapide
                    const newRef = generateReference(type, resp);
                    setCircuit(prev => ({ 
                        ...prev, 
                        toClient: resp, 
                        nomClient: null,
                        reference: newRef 
                    }));
                }
                if (type === 'typeFrais') setNewTypeFraisDialog(false);
                if (type === 'excursion') {
                    setNewExcursionDialog(false);
                }

                // Sélection automatique
                if (activeQuickAdd.table) {
                    const { table, rowIdx, field } = activeQuickAdd;
                    const newValue = resp.id;

                    if (table === 'locations') {
                        setLocations(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: newValue } : row));
                    } else if (table === 'excursions') {
                        setExcursions(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: newValue } : row));
                    } else if (table === 'transferts') {
                        setTransferts(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: newValue } : row));
                    } else if (table === 'hotels') {
                        setCircuits(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: newValue } : row));
                    } else if (table === 'envelops') {
                        setEnvelops(prev => prev.map((row, i) => i === rowIdx ? { ...row, [field]: newValue } : row));
                    }
                } else if (activeQuickAdd.field) {
                    // Sélection pour les champs principaux du dossier
                    const { field } = activeQuickAdd;
                    setCircuit(prev => ({ ...prev, [field]: resp }));
                }

                // Réinitialiser le contexte
                setActiveQuickAdd({ table: null, rowIdx: null, field: null });

                notify(`${action.label} "${itemName}" enregistré avec succès !`, 'success');
            }
        );
    };

    // Calcul des frais automatiques
    useEffect(() => {
        let circuitFrais = {
            frais: "HEBERGEMENT",
            montant: circuits.reduce((sum, current) => {
                return sum + (current.prixUnitaire * current.pax * current.nuitee);
            }, 0)
        };
        
        let excursionsFrais = {
            frais: "EXCURSION",
            montant: excursions.reduce((sum, current) => {
                return sum + (current.prixUnitaire * current.pax);
            }, 0)
        };

        let locationFrais = {
            frais: "LOCATION",
            montant: locations.reduce((sum, current) => {
                return sum + (current.prixUnitaire * current.nombreJour);
            }, 0)
        };

        let transfertFrais = {
            frais: "TRANSFERT",
            montant: transferts.reduce((sum, current) => {
                return sum + (current.prixUnitaire * current.pax);
            }, 0)
        };

        let depouillementFrais = {
            frais: "DEPOUILLEMENT",
            montant: envelops.reduce((sum, current) => {
                return sum + Number(current.depouillement);
            }, 0)
        };

        setFrais(frais.map((frai) => {
            if (frai.frais === "HEBERGEMENT") return circuitFrais;
            if (frai.frais === "TRANSFERT") return transfertFrais;
            if (frai.frais === "LOCATION") return locationFrais;
            if (frai.frais === "EXCURSION") return excursionsFrais;
            if (frai.frais === "DEPOUILLEMENT") return depouillementFrais;
            return frai;
        }));
    }, [locations, transferts, excursions, circuits, envelops]);

    useEffect(() => {
        const total = frais.reduce((sum, current) => {
            return sum + Number(current.montant);
        }, 0);
        setMontant(total);
    }, [frais]);
    
    // --- Synchronisation automatique Chauffeur/Guide/Véhicule ---
    useEffect(() => {
        if (!circuit.chauffeur) return;
        const chauffeurId = circuit.chauffeur.id;
        setExcursions(prev => prev.map(e => ({ ...e, chauffeur: e.chauffeur || chauffeurId })));
        setTransferts(prev => prev.map(t => ({ ...t, chauffeur: t.chauffeur || chauffeurId })));
        setLocations(prev => prev.map(l => ({ ...l, chauffeur: l.chauffeur || chauffeurId })));
    }, [circuit.chauffeur]);

    useEffect(() => {
        if (!circuit.guide) return;
        const guideId = circuit.guide.id;
        setExcursions(prev => prev.map(e => ({ ...e, guide: e.guide || guideId })));
        setTransferts(prev => prev.map(t => ({ ...t, guide: t.guide || guideId })));
        setLocations(prev => prev.map(l => ({ ...l, guide: l.guide || guideId })));
    }, [circuit.guide]);

    useEffect(() => {
        if (!circuit.vehicule) return;
        const vehiculeId = circuit.vehicule.id;
        setTransferts(prev => prev.map(t => ({ ...t, vehicule: t.vehicule || vehiculeId })));
        setLocations(prev => prev.map(l => ({ ...l, vehicule: l.vehicule || vehiculeId })));
    }, [circuit.vehicule]);
    // ---------------------------------------------------

    // Calcul automatique du nombre de jours et synchro date vol
    useEffect(() => {
        if (circuit.dateDebut && circuit.dateFin) {
            const debut = dayjs(circuit.dateDebut).startOf('day');
            const fin = dayjs(circuit.dateFin).startOf('day');
            if (fin.isValid() && debut.isValid() && (fin.isAfter(debut) || fin.isSame(debut))) {
                const jours = fin.diff(debut, 'day') + 1; // +1 car le jour de début est inclus
                
                setCircuit(prev => ({ 
                    ...prev, 
                    nombreJour: jours,
                    dateVolArrivee: debut,
                    dateVolDepart: fin
                }));
            }
        } else if (circuit.dateDebut) {
            const debut = dayjs(circuit.dateDebut).startOf('day');
            if (debut.isValid()) {
                setCircuit(prev => ({ ...prev, dateVolArrivee: debut }));
            }
        } else if (circuit.dateFin) {
            const fin = dayjs(circuit.dateFin).startOf('day');
            if (fin.isValid()) {
                setCircuit(prev => ({ ...prev, dateVolDepart: fin }));
            }
        }
    }, [circuit.dateDebut, circuit.dateFin]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
            {(!!id && !circuit.id) ?
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
                :
                <Box sx={{ 
                    minHeight: '100vh',
                    background: `linear-gradient(135deg, ${alpha(getTypeColor(), 0.05)} 0%, ${alpha(COLORS.primary, 0.05)} 100%)`,
                    p: 3
                }}>
                    <Breadcrumb customItems={breadcrumbItems} />

                    {/* Header moderne */}
                    <Fade in timeout={800}>
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 3, 
                                mb: 3, 
                                background: `linear-gradient(135deg, ${getTypeColor()} 0%, ${COLORS.primary} 100%)`,
                                color: 'white',
                                borderRadius: 3,
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                                <Box sx={{ fontSize: 180 }}>{getTypeIcon()}</Box>
                            </Box>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                                <IconButton 
                                    onClick={() => navigateTo(`/dossiers/${type}`)}
                                    sx={{ 
                                        bgcolor: alpha('#fff', 0.2),
                                        color: 'white',
                                        '&:hover': { bgcolor: alpha('#fff', 0.3) }
                                    }}
                                >
                                    <West />
                                </IconButton>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {getTypeIcon()}
                                        {getPageTitle()}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {getPageDescription()}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Fade>

                    {/* Informations principales */}
                    <Zoom in timeout={600}>
                        <Card elevation={2} sx={{ mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Description color="primary" />
                                    Informations du dossier
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            freeSolo
                                            options={toClients || []}
                                            getOptionLabel={(option) => {
                                                if (typeof option === 'string') return option;
                                                if (option.isAction) return option.libelle;
                                                return option.libelle || '';
                                            }}
                                            value={circuit.toClient ? (toClients.find(c => c.id === circuit.toClient?.id) || circuit.toClient) : (circuit.nomClient || null)}
                                            isOptionEqualToValue={(option, value) => {
                                                if (option.isAction && value.isAction) return option.libelle === value.libelle;
                                                if (option.isAction || value.isAction) return false;
                                                return option.id === value.id;
                                            }}
                                            renderOption={(props, option) => {
                                                const { key, ...otherProps } = props;
                                                return (
                                                    <li {...otherProps} key={option.key || option.id || key}>
                                                        {option.libelle}
                                                    </li>
                                                );
                                            }}
                                            onChange={(e, value) => {
                                                if (typeof value === 'string' && value.trim()) {
                                                    // Cas freeSolo pur (si l'utilisateur tape et appuie sur entrée)
                                                    const newRef = value.trim() ? generateReference(type, value.trim(), null) : '';
                                                    setCircuit({
                                                        ...circuit,
                                                        toClient: null,
                                                        nomClient: value.trim(),
                                                        clientType: null,
                                                        reference: newRef
                                                    });
                                                } else if (value && value.isAction === 'partenaire') {
                                                    setTimeout(() => {
                                                        setNewClientName(value.inputValue);
                                                        setActiveQuickAdd({ table: null, rowIdx: null, field: 'toClient' });
                                                        setNewClientDialog(true);
                                                    }, 0);
                                                } else if (value && value.isAction === 'individuel') {
                                                    // Client individuel : référence IND + rooming auto-rempli
                                                    const nomSaisi = value.inputValue || '';
                                                    const newRef = nomSaisi ? generateReference(type, nomSaisi, 'individuel') : '';
                                                    // Décomposer le nom saisi en prénom / nom
                                                    const parts = nomSaisi.trim().split(/\s+/);
                                                    const prenom = parts[0] || '';
                                                    const nom = parts.slice(1).join(' ');
                                                    const autoRooming = [{ civilite: '', prenom, nom }];
                                                    setRooming(autoRooming);
                                                    setCircuit({
                                                        ...circuit,
                                                        toClient: null,
                                                        nomClient: nomSaisi,
                                                        clientType: 'individuel',
                                                        reference: newRef
                                                    });
                                                } else {
                                                    const newRef = value ? generateReference(type, value, null) : '';
                                                    setCircuit({
                                                        ...circuit,
                                                        toClient: value,
                                                        nomClient: null,
                                                        clientType: 'partenaire',
                                                        // Toujours régénérer la référence à chaque changement de client
                                                        reference: newRef,
                                                    });
                                                }
                                            }}
                                            filterOptions={(options, { inputValue }) => {
                                                if (!inputValue) return options;
                                                const lower = inputValue.toLowerCase();
                                                const filtered = options.filter(o => (o.libelle || '').toLowerCase().includes(lower));
                                                
                                                // S'il n'y a pas de match exact, on ajoute les options dynamiques avec des libelles uniques
                                                const isExisting = options.some(option => inputValue.toLowerCase() === (option.libelle || '').toLowerCase());
                                                if (inputValue !== '' && !isExisting) {
                                                    const timestamp = Date.now();
                                                    const random = Math.random().toString(36).substr(2, 9);
                                                    
                                                    filtered.push({
                                                        inputValue: inputValue,
                                                        libelle: `Créer "${inputValue}" comme Client Partenaire`,
                                                        isAction: 'partenaire',
                                                        key: `create-partenaire-${timestamp}-${random}`
                                                    });
                                                    filtered.push({
                                                        inputValue: inputValue,
                                                        libelle: `Utiliser "${inputValue}" comme Client Individuel`,
                                                        isAction: 'individuel',
                                                        key: `use-individuel-${timestamp}-${random}`
                                                    });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="Client"
                                                    size="small"
                                                    placeholder="Sélectionner ou créer un client..."
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: <People sx={{ mr: 1, color: 'action.active' }} />
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Référence"
                                            value={circuit.reference || ''}
                                            onChange={(e) => setCircuit({...circuit, reference: e.target.value})}
                                            size="small"
                                            InputProps={{
                                                startAdornment: <Receipt sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            freeSolo
                                            options={[...(programmes || []), { isAction: 'programme', libelle: '+ Ajouter un nouveau programme', inputValue: '' }]}
                                            getOptionLabel={(option) => {
                                                if (option.isAction) return option.libelle;
                                                return option.libelle || '';
                                            }}
                                            value={circuit.programme || null}
                                            onChange={(e, value) => {
                                                if (value?.isAction === 'programme') {
                                                    setNewProgrammeName(value.inputValue || '');
                                                    setNewProgrammeDialog(true);
                                                } else if (typeof value === 'string' && value.trim()) {
                                                    setNewProgrammeName(value.trim());
                                                    setNewProgrammeDialog(true);
                                                } else {
                                                    setCircuit({...circuit, programme: value});
                                                }
                                            }}
                                            filterOptions={(options, { inputValue }) => {
                                                const lower = inputValue.toLowerCase();
                                                const filtered = options.filter(o => (o.libelle || '').toLowerCase().includes(lower));
                                                if (inputValue !== '' && !options.some(o => o.libelle === inputValue)) {
                                                    filtered.push({ isAction: 'programme', libelle: `+ Créer "${inputValue}"`, inputValue });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="Programme"
                                                    size="small"
                                                    placeholder="Sélectionner ou créer un nouveau programme"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: <Event sx={{ mr: 1, color: 'action.active' }} />
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <DateField
                                            label="Date début (Arrivée équipe)"
                                            value={circuit.dateDebut ? dayjs(circuit.dateDebut) : undefined}
                                            onChange={(value) => setCircuit({...circuit, dateDebut: value})}
                                            fullWidth
                                            size="small"
                                            format="DD/MM/YYYY"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <DateField
                                            label="Date fin"
                                            value={circuit.dateFin ? dayjs(circuit.dateFin) : undefined}
                                            onChange={(value) => setCircuit({...circuit, dateFin: value})}
                                            fullWidth
                                            size="small"
                                            format="DD/MM/YYYY"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Nombre de jours"
                                            type="number"
                                            value={circuit.nombreJour || ''}
                                            onChange={(e) => setCircuit({...circuit, nombreJour: e.target.value})}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Nombre de participants"
                                            type="number"
                                            value={circuit.pax || ''}
                                            onChange={(e) => setCircuit({...circuit, pax: e.target.value})}
                                            size="small"
                                            InputProps={{
                                                startAdornment: <People sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Conditions de paiement"
                                            select
                                            value={circuit.methodePaiement || 'Autre'}
                                            onChange={(e) => setCircuit({...circuit, methodePaiement: e.target.value})}
                                            size="small"
                                            SelectProps={{
                                                MenuProps: {
                                                    sx: { '& .MuiPaper-root': { maxHeight: 200 } }
                                                }
                                            }}
                                        >
                                            <MenuItem value="Espèces">Espèces</MenuItem>
                                            <MenuItem value="Chèque">Chèque</MenuItem>
                                            <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
                                            <MenuItem value="Virement">Virement</MenuItem>
                                            <MenuItem value="Autre">Autre</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 3 }} />
                                
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FlightTakeoff color="primary" />
                                    Informations de vol
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>VOL ARRIVÉE</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Compagnie"
                                            value={circuit.compagnieVolArrivee || ''}
                                            onChange={(e) => setCircuit({...circuit, compagnieVolArrivee: e.target.value})}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="N° de vol"
                                            value={circuit.numeroVolArrivee || ''}
                                            onChange={(e) => setCircuit({...circuit, numeroVolArrivee: e.target.value})}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DateField
                                            fullWidth
                                            label="Date d'arrivée vol"
                                            value={circuit.dateVolArrivee ? dayjs(circuit.dateVolArrivee) : null}
                                            readOnly
                                            size="small"
                                            format="DD/MM/YYYY"
                                            helperText="Synchronisé avec la date de début du dossier"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TimeField
                                            fullWidth
                                            label="Horaire vol arrivée"
                                            value={circuit.heureVolArrivee ? dayjs(circuit.heureVolArrivee) : null}
                                            onChange={(val) => setCircuit({...circuit, heureVolArrivee: val})}
                                            size="small"
                                            format="HH:mm"
                                            helperText="Ex: 14:30"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>VOL DÉPART</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Compagnie"
                                            value={circuit.compagnieVolDepart || ''}
                                            onChange={(e) => setCircuit({...circuit, compagnieVolDepart: e.target.value})}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="N° de vol"
                                            value={circuit.numeroVolDepart || ''}
                                            onChange={(e) => setCircuit({...circuit, numeroVolDepart: e.target.value})}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DateField
                                            fullWidth
                                            label="Date de départ vol"
                                            value={circuit.dateVolDepart ? dayjs(circuit.dateVolDepart) : null}
                                            readOnly
                                            size="small"
                                            format="DD/MM/YYYY"
                                            helperText="Synchronisé avec la date de fin du dossier"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TimeField
                                            fullWidth
                                            label="Horaire vol départ"
                                            value={circuit.heureVolDepart ? dayjs(circuit.heureVolDepart) : null}
                                            onChange={(val) => setCircuit({...circuit, heureVolDepart: val})}
                                            size="small"
                                            format="HH:mm"
                                            helperText="Ex: 09:15"
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Zoom>

                    {/* ÉQUIPE & LOGISTIQUE */}
                    <Zoom in timeout={700}>
                        <Card elevation={2} sx={{ mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <People color="primary" />
                                    Équipe & Logistique
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Autocomplete
                                            options={[...(chauffeurs || []), { isAction: 'chauffeur', nomComplet: '+ Ajouter un chauffeur', inputValue: '' }]}
                                            getOptionLabel={(option) => option.nomComplet || ''}
                                            value={circuit.chauffeur || null}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(e, value) => {
                                                if (value?.isAction === 'chauffeur') {
                                                    setNewChauffeurName(value.inputValue || '');
                                                    setActiveQuickAdd({ table: null, rowIdx: null, field: 'chauffeur' });
                                                    setNewChauffeurDialog(true);
                                                } else {
                                                    setCircuit({ ...circuit, chauffeur: value });
                                                }
                                            }}
                                            filterOptions={(options, { inputValue }) => {
                                                const filtered = options.filter(o => (o.nomComplet || '').toLowerCase().includes(inputValue.toLowerCase()));
                                                if (inputValue !== '' && !options.some(o => o.nomComplet === inputValue)) {
                                                    filtered.push({ isAction: 'chauffeur', nomComplet: `+ Créer "${inputValue}"`, inputValue });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Chauffeur Accomp." size="small" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Autocomplete
                                            options={[...(guides || []), { isAction: 'guide', nomComplet: '+ Ajouter un guide', inputValue: '' }]}
                                            getOptionLabel={(option) => option.nomComplet || ''}
                                            value={circuit.guide || null}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(e, value) => {
                                                if (value?.isAction === 'guide') {
                                                    setNewGuideName(value.inputValue || '');
                                                    setActiveQuickAdd({ table: null, rowIdx: null, field: 'guide' });
                                                    setNewGuideDialog(true);
                                                } else {
                                                    setCircuit({ ...circuit, guide: value });
                                                }
                                            }}
                                            filterOptions={(options, { inputValue }) => {
                                                const filtered = options.filter(o => (o.nomComplet || '').toLowerCase().includes(inputValue.toLowerCase()));
                                                if (inputValue !== '' && !options.some(o => o.nomComplet === inputValue)) {
                                                    filtered.push({ isAction: 'guide', nomComplet: `+ Créer "${inputValue}"`, inputValue });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Guide Certifié" size="small" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Autocomplete
                                            options={[...(vehicules || []), { isAction: 'vehicule', matricule: '+ Ajouter un véhicule', inputValue: '' }]}
                                            getOptionLabel={(option) => {
                                                if (option.isAction) return option.matricule;
                                                return `${option.marque || ''} - ${option.modele || ''} - ${option.matricule || ''}`.replace(/^ - | - $/g, '');
                                            }}
                                            value={circuit.vehicule || null}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(e, value) => {
                                                if (value?.isAction === 'vehicule') {
                                                    setNewVehiculeName(value.inputValue || '');
                                                    setActiveQuickAdd({ table: null, rowIdx: null, field: 'vehicule' });
                                                    setNewVehiculeDialog(true);
                                                } else {
                                                    setCircuit({ ...circuit, vehicule: value });
                                                }
                                            }}
                                            filterOptions={(options, { inputValue }) => {
                                                const lower = inputValue.toLowerCase();
                                                const filtered = options.filter(o => 
                                                    (o.matricule || '').toLowerCase().includes(lower) || 
                                                    (o.marque || '').toLowerCase().includes(lower) ||
                                                    (o.modele || '').toLowerCase().includes(lower)
                                                );
                                                if (inputValue !== '' && !options.some(o => o.matricule === inputValue)) {
                                                    filtered.push({ isAction: 'vehicule', matricule: `+ Créer "${inputValue}"`, inputValue });
                                                }
                                                return filtered;
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Nos véhicules" size="small" />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Zoom>


                    {/* Résumé Financier compact — AU-DESSUS DES ONGLETS */}
                    <Zoom in timeout={900}>
                        <Paper
                            elevation={2}
                            sx={{
                                mb: 2,
                                p: 1.5,
                                background: `linear-gradient(90deg, ${alpha(getTypeColor(), 0.07)}, ${alpha(COLORS.primary, 0.04)})`,
                                borderLeft: `4px solid ${getTypeColor()}`
                            }}
                        >
                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, borderRight: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Env. guide :</Typography>
                                    <Typography variant="body2" fontWeight="bold" color={COLORS.secondary}>
                                        {(() => {
                                            const p = envelops.reduce((s, c) => ({ m: isNaN(s.m + Number(c.montant)) ? 0 : s.m + Number(c.montant) }), { m: 0 }).m;
                                            return priceFormatter(p);
                                        })()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, borderRight: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Total débours :</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="error.main">
                                        {(() => {
                                            const p = envelops.reduce((s, c) => ({ d: isNaN(s.d + Number(c.depouillement)) ? 0 : s.d + Number(c.depouillement) }), { d: 0 }).d;
                                            return priceFormatter(p);
                                        })()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, borderRight: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Montant CFA :</Typography>
                                    <Typography variant="body2" fontWeight="bold" color={getTypeColor()}>
                                        {priceFormatter(montant)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Montant Euro :</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                        {toEuro(montant)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Zoom>

                    {/* Tabs pour les différentes sections */}
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Tabs
                            value={tabs.indexOf(tab) >= 0 ? tabs.indexOf(tab) : 0}
                            onChange={(e, newValue) => setTab(tabs[newValue])}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    minHeight: 60,
                                    fontWeight: 'bold',
                                    textTransform: 'none'
                                },
                                '& .Mui-selected': {
                                    color: getTypeColor()
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: getTypeColor(),
                                    height: 3
                                }
                            }}
                        >
                            {tabs.map((tabName, index) => (
                                <Tab 
                                    key={index}
                                    label={tabName} 
                                    sx={{ fontSize: '1rem' }}
                                />
                            ))}
                        </Tabs>

                        <Divider />

                        {/* Tab Content */}
                        <Box sx={{ p: 3 }}>
                            {tab === 'Hebergements' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Hotel color="primary" />
                                                Hébergements
                                                <Chip label={`${circuits.length} hôtel(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Circuits 
                                            circuits={circuits} 
                                            onCircuitUpdate={onCircuitUpdate} 
                                            handleQuickAdd={handleQuickAdd}
                                            setNewHotelName={setNewHotelName}
                                            setNewHotelDialog={setNewHotelDialog}
                                            dossierDates={{ dateDebut: circuit.dateDebut, dateFin: circuit.dateFin }}
                                            setActiveQuickAdd={setActiveQuickAdd}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Rooming' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <People color="primary" />
                                                Rooming
                                                <Chip label={`${rooming.length} personne(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Rooming 
                                            rooming={rooming} 
                                            onRoomingUpdate={onRoomingUpdate} 
                                            onRoomingTypeChanged={onRoomingTypeChanged} 
                                            pax={circuit.pax}
                                            type={circuit.grouped}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Excursions' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Hiking color="primary" />
                                                Excursions
                                                <Chip label={`${excursions.length} excursion(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Excursions 
                                            excursions={excursions} 
                                            onExcursionUpdate={onExcursionUpdate} 
                                            chauffeur={circuit.chauffeur?.id} 
                                            guide={circuit.guide?.id}
                                            handleQuickAdd={handleQuickAdd}
                                            setNewExcursionName={setNewExcursionName}
                                            setNewExcursionDialog={setNewExcursionDialog}
                                            setNewChauffeurName={setNewChauffeurName}
                                            setNewChauffeurDialog={setNewChauffeurDialog}
                                            setNewGuideName={setNewGuideName}
                                            setNewGuideDialog={setNewGuideDialog}
                                            dossierDates={{ dateDebut: circuit.dateDebut, dateFin: circuit.dateFin }}
                                            setActiveQuickAdd={setActiveQuickAdd}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Transferts' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <DirectionsCar color="primary" />
                                                Transferts
                                                <Chip label={`${transferts.length} transfert(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Transferts 
                                            transferts={transferts} 
                                            onTransfertUpdate={onTransfertUpdate} 
                                            chauffeur={circuit.chauffeur?.id} 
                                            guide={circuit.guide?.id}
                                            vehicule={circuit.vehicule?.id}
                                            handleQuickAdd={handleQuickAdd}
                                            setNewChauffeurName={setNewChauffeurName}
                                            setNewChauffeurDialog={setNewChauffeurDialog}
                                            setNewVehiculeName={setNewVehiculeName}
                                            setNewVehiculeDialog={setNewVehiculeDialog}
                                            setNewGuideName={setNewGuideName}
                                            setNewGuideDialog={setNewGuideDialog}
                                            dossierDates={{ dateDebut: circuit.dateDebut, dateFin: circuit.dateFin }}
                                            setActiveQuickAdd={setActiveQuickAdd}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Locations' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <DirectionsCar color="primary" />
                                                Locations
                                                <Chip label={`${locations.length} location(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Locations 
                                            locations={locations} 
                                            onLocationUpdate={onLocationUpdate}
                                            chauffeur={circuit.chauffeur?.id} 
                                            guide={circuit.guide?.id}
                                            vehicule={circuit.vehicule?.id}
                                            handleQuickAdd={handleQuickAdd}
                                            setNewAgenceName={setNewAgenceName}
                                            setNewAgenceDialog={setNewAgenceDialog}
                                            setNewChauffeurName={setNewChauffeurName}
                                            setNewChauffeurDialog={setNewChauffeurDialog}
                                            setNewVehiculeName={setNewVehiculeName}
                                            setNewVehiculeDialog={setNewVehiculeDialog}
                                            setNewGuideName={setNewGuideName}
                                            setNewGuideDialog={setNewGuideDialog}
                                            dossierDates={{ dateDebut: circuit.dateDebut, dateFin: circuit.dateFin }}
                                            setActiveQuickAdd={setActiveQuickAdd}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Enveloppe' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Description color="primary" />
                                                Enveloppe
                                                <Chip label={`${envelops.length} enveloppe(s)`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <Enveloppe 
                                            enveloppes={envelops} 
                                            onEnvelopUpdate={onEnvelopUpdate}
                                        />
                                    </Stack>
                                </Box>
                            )}

                            {tab === 'Synthèse' && (
                                <Box>
                                    <Stack spacing={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <AccountBalance color="primary" />
                                                Synthèse
                                                <Chip label={`${frais.length} postes`} size="small" color="primary" variant="outlined" />
                                            </Typography>
                                        </Box>
                                        <ListFrais frais={frais} onFraisUpdate={onFraisUpdate} />
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Paper>


                    {/* Actions */}
                    <Stack direction="row" justifyContent="flex-end" spacing={2}>
                            <Button
                            variant="outlined"
                            size="large"
                            startIcon={<West />}
                            onClick={() => {
                                if (window.confirm("Voulez-vous vraiment quitter ? Les modifications non enregistrées seront perdues.")) {
                                    navigateTo(`/dossiers/${type}`);
                                }
                            }}
                            sx={{ px: 4 }}
                        >
                            FERMER
                        </Button>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isEdit ? <Save /> : <Add />)}
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{ 
                                px: 4,
                                background: `linear-gradient(45deg, ${getTypeColor()}, ${COLORS.primary})`,
                                '&:hover': {
                                    background: `linear-gradient(45deg, ${alpha(getTypeColor(), 0.9)}, ${alpha(COLORS.primary, 0.9)})`
                                }
                            }}
                        >
                            {loading ? (isEdit ? 'Enregistrement...' : 'Création en cours...') : (isEdit ? 'Enregistrer les modifications' : 'Créer le dossier')}
                        </Button>
                    </Stack>

                    {/* Dialog pour client */}
                    <Dialog open={openClientModal} onClose={() => setOpenClientModal(false)} maxWidth="md" fullWidth>
                        <DialogContent>
                            <ClientForm onClose={() => setOpenClientModal(false)} />
                        </DialogContent>
                    </Dialog>

                    {/* Dialog pour nouveau programme */}
                    <Dialog open={newProgrammeDialog} onClose={() => setNewProgrammeDialog(false)} maxWidth="sm" fullWidth>
                        <DialogContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Créer un nouveau programme
                            </Typography>
                            <TextField
                                fullWidth
                                label="Nom du programme"
                                value={newProgrammeName}
                                onChange={(e) => setNewProgrammeName(e.target.value)}
                                size="small"
                                sx={{ mb: 2 }}
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button onClick={() => setNewProgrammeDialog(false)}>
                                    FERMER
                                </Button>
                                <Button variant="contained" onClick={handleCreateProgramme}>
                                    Créer
                                </Button>
                            </Stack>
                        </DialogContent>
                    </Dialog>

                    {/* Dialog pour nouvelle excursion */}
                    <Dialog open={newExcursionDialog} onClose={() => setNewExcursionDialog(false)} maxWidth="sm" fullWidth>
                        <DialogContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Créer une nouvelle excursion
                            </Typography>
                            <TextField
                                fullWidth
                                label="Nom de l'excursion"
                                value={newExcursionName}
                                onChange={(e) => setNewExcursionName(e.target.value)}
                                size="small"
                                sx={{ mb: 2 }}
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button onClick={() => setNewExcursionDialog(false)}>
                                    FERMER
                                </Button>
                                <Button variant="contained" onClick={handleCreateExcursion}>
                                    Créer
                                </Button>
                            </Stack>
                        </DialogContent>
                    </Dialog>

                    {/* Dialogs pour création rapide */}
                    <Dialog open={newChauffeurDialog} onClose={() => setNewChauffeurDialog(false)}>
                        <DialogTitle>Créer un nouveau chauffeur</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new Chauffeur()} 
                                toUpdate={{ nomComplet: newChauffeurName }}
                                onSubmit={(data) => handleQuickAdd('chauffeur', data)}
                                onCancel={() => setNewChauffeurDialog(false)}
                                mandatories={["nomComplet", "telephone"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newGuideDialog} onClose={() => setNewGuideDialog(false)}>
                        <DialogTitle>Créer un nouveau guide</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new Guide()} 
                                toUpdate={{ nomComplet: newGuideName }}
                                onSubmit={(data) => handleQuickAdd('guide', data)}
                                onCancel={() => setNewGuideDialog(false)}
                                mandatories={["nomComplet", "telephone"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newVehiculeDialog} onClose={() => setNewVehiculeDialog(false)}>
                        <DialogTitle>Créer un nouveau véhicule</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new Vehicule()} 
                                toUpdate={{ matricule: newVehiculeName }}
                                onSubmit={(data) => handleQuickAdd('vehicule', data)}
                                onCancel={() => setNewVehiculeDialog(false)}
                                mandatories={["matricule", "marque"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newHotelDialog} onClose={() => setNewHotelDialog(false)}>
                        <DialogTitle>Créer un nouvel hôtel</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new HotelSchema()} 
                                toUpdate={{ nom: newHotelName }}
                                onSubmit={(data) => handleQuickAdd('hotel', data)}
                                onCancel={() => setNewHotelDialog(false)}
                                mandatories={["nom", "code", "telephone", "courriel"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newAgenceDialog} onClose={() => setNewAgenceDialog(false)}>
                        <DialogTitle>Créer une nouvelle agence</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new Agence()} 
                                toUpdate={{ nom: newAgenceName }}
                                onSubmit={(data) => handleQuickAdd('agence', data)}
                                onCancel={() => setNewAgenceDialog(false)}
                                mandatories={["nom"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newTypeFraisDialog} onClose={() => setNewTypeFraisDialog(false)}>
                        <DialogTitle>Créer un nouveau type de frais</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new BaseParameter()} 
                                toUpdate={{ libelle: newTypeFraisName }}
                                onSubmit={(data) => handleQuickAdd('typeFrais', data)}
                                onCancel={() => setNewTypeFraisDialog(false)}
                                mandatories={["libelle"]}
                            />
                        </DialogContent>
                    </Dialog>

                    <Dialog open={newClientDialog} onClose={() => setNewClientDialog(false)} maxWidth="md" fullWidth>
                        <DialogTitle>Créer un nouveau client</DialogTitle>
                        <DialogContent>
                            <QuickAddForm 
                                entity={new ToClient()} 
                                toUpdate={{ libelle: newClientName, code: newClientName ? newClientName.substring(0, 3).toUpperCase() : '' }}
                                onSubmit={(data) => handleQuickAdd('client', data)}
                                onCancel={() => setNewClientDialog(false)}
                                mandatories={["libelle", "code", "pays"]}
                            />
                        </DialogContent>
                    </Dialog>
                </Box>
            }
        </LocalizationProvider>
    );
};

export default AjoutCircuitPage;
